import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import roleApi from "../../../services/role";
import menuApi from "../../../services/menu";

const useStyles = makeStyles({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    position: "relative",
    width: "80%",
    // height: "80%",
    maxWidth: "600px",
    backgroundColor: "white",
    padding: "20px",
    boxShadow: 24,
    borderRadius: "8px",
    outline: "none",
    overflowY: "auto",
    overflowX: "hidden",
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1,
  },
  table: {
    marginTop: "20px",
  },
  tableCell: {
    textAlign: "center",
    // display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  subMenu: {
    paddingLeft: "20px",
  },
});

const RoleChartModal = ({ open, onClose, roleSelect, setSelectedRoleUuid }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedCells, setSelectedCells] = useState({});
  const [menuList, setMenuList] = useState([]);
  const [roleID, setRoleID] = useState();
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [createdRole, setCreatedRole] = useState();
  const [menuMap, setMenuMap] = useState(false);

  const getAllMenuList = useCallback(() => {
    dispatch(showLoader());

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status) {
        setMenuList(res.data.data);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error("Error fetching roles:", err);
    };
    menuApi.getAllMenu().then(onSuccess, onFailure);
  }, [dispatch]);

  useEffect(() => {
    if (open) {
      getAllMenuList();
    }
  }, [open, getAllMenuList]);

  const handleCellChange = (menuName, accessType) => {
    const cellKey = `${menuName}-admin`;
    const newAccessTypes = {
      full: false,
      read: false,
      none: false,
    };

    if (accessType.full) {
      newAccessTypes.full = true;
    } else if (accessType.read) {
      newAccessTypes.read = true;
    } else if (accessType.none) {
      newAccessTypes.none = true;
    }

    setSelectedCells((prev) => ({
      ...prev,
      [cellKey]: newAccessTypes,
    }));

    if (accessType.none) {
      menuList.forEach((menu) => {
        if (menu.name === menuName && menu.submenus) {
          menu.submenus.forEach((sub) => {
            const subCellKey = `${menu.name}-${sub.name}-admin`;
            setSelectedCells((prev) => ({
              ...prev,
              [subCellKey]: { full: false, read: false, none: true },
            }));
          });
        }
      });
    } else if (!accessType.none) {
      menuList.forEach((menu) => {
        if (menu.name === menuName && menu.submenus) {
          menu.submenus.forEach((sub) => {
            const subCellKey = `${menu.name}-${sub.name}-admin`;
            setSelectedCells((prev) => ({
              ...prev,
              [subCellKey]: { full: false, read: false, none: false },
            }));
          });
        }
      });
    }
  };

  const logSelectedMenuUUIDs = () => {
    const newSelectedMenus = [];

    menuList.forEach((menu) => {
      const mainCellKey = `${menu.name}-admin`;
      const accessTypes = selectedCells[mainCellKey];
      if (accessTypes) {
        newSelectedMenus.push({
          menu_uuid: menu.uuid,
          access_type: accessTypes.full
            ? "read_write"
            : accessTypes.read
            ? "read"
            : "none",
        });
      }

      if (menu.submenus && menu.submenus.length > 0) {
        menu.submenus.forEach((sub) => {
          const subCellKey = `${menu.name}-${sub.name}-admin`;
          const accessTypesSub = selectedCells[subCellKey];
          if (accessTypesSub) {
            newSelectedMenus.push({
              menu_uuid: sub.uuid,
              access_type: accessTypesSub.full
                ? "read_write"
                : accessTypesSub.read
                ? "read"
                : "none",
            });
          }
        });
      }
    });

    if (newSelectedMenus.length === 0) {
      dispatch(
        showSnackbar({
          message: "Please select at least one menu option.",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "warning",
        })
      );
      return;
    }

    setSelectedMenus(newSelectedMenus);
    setSelectedCells({});
    setMenuMap(false);
  };

  const menuSubmit = () => {
    const data = {
      role_uuid: roleID,
      menus: selectedMenus,
    };

    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Role Name Required!",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        onClose();
        roleSelect();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Invalid Credentials",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    roleApi.roleMap(data).then(onSuccess, onFailure);
  };

  useEffect(() => {
    if (selectedMenus.length > 0) {
      menuSubmit();
    }
  }, [selectedMenus]);

  const handleSubmit = (formData) => {
    const data = {
      role: formData?.role_name,
      role_description: formData?.role_name,
    };

    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Role Name Required!",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        setRoleID(res?.data?.data?.uuid);
        setSelectedRoleUuid(res?.data?.data);
        setCreatedRole(res?.data?.data?.role);
        setMenuMap(true);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Invalid Credentials",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setMenuMap(false);
    };
    roleApi.createRole(data).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      role_name: "",
    },
    validationSchema: Yup.object({
      role_name: Yup.string()
        .required("Role Name is required")
        .min(2, "Role Name should be at least 2 characters"),
    }),
    onSubmit: handleSubmit,
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
    setMenuMap(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={(event) => event.stopPropagation()}
        aria-labelledby="role-chart-modal-title"
        aria-describedby="role-chart-modal-description"
        className={classes.modal}
      >
        <Box
          className={classes.modalContent}
          onClick={(e) => e.stopPropagation()}
          sx={{ height: menuMap ? "80%" : "auto" }}
        >
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            id="role-chart-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Custom Role
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={9}>
              <TextField
                name="role_name"
                label="Role Name"
                variant="outlined"
                fullWidth
                value={formik.values.role_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.role_name && Boolean(formik.errors.role_name)
                }
                helperText={formik.touched.role_name && formik.errors.role_name}
              />
            </Grid>

            {!menuMap && (
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={formik.handleSubmit}
                  fullWidth
                >
                  Add Role
                </Button>
              </Grid>
            )}
          </Grid>
          {menuMap && (
            <>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableCell}>Roles</TableCell>
                    <TableCell className={classes.tableCell}>
                      Full Access
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      Read Access
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      No Access
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menuList.map((role) => (
                    <React.Fragment key={role.id}>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          {role.name !== "" ? role.name : null}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    selectedCells[`${role.name}-admin`]
                                      ? selectedCells[`${role.name}-admin`].full
                                      : false
                                  }
                                  onChange={(e) =>
                                    handleCellChange(role.name, {
                                      full: e.target.checked,
                                    })
                                  }
                                  sx={{ margin: 0 }}
                                />
                              }
                            />
                          </Box>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    selectedCells[`${role.name}-admin`]
                                      ? selectedCells[`${role.name}-admin`].read
                                      : false
                                  }
                                  onChange={(e) =>
                                    handleCellChange(role.name, {
                                      read: e.target.checked,
                                    })
                                  }
                                  sx={{ margin: 0 }}
                                  disabled={
                                    role.dropdown === true ||
                                    (selectedCells[`${role.name}-admin`] &&
                                      selectedCells[`${role.name}-admin`].full)
                                  }
                                />
                              }
                            />
                          </Box>
                        </TableCell>

                        <TableCell className={classes.tableCell}>
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    selectedCells[`${role.name}-admin`]
                                      ? selectedCells[`${role.name}-admin`].none
                                      : false
                                  }
                                  onChange={(e) =>
                                    handleCellChange(role.name, {
                                      none: e.target.checked,
                                    })
                                  }
                                  sx={{ margin: 0 }}
                                  disabled={
                                    selectedCells[`${role.name}-admin`] &&
                                    (selectedCells[`${role.name}-admin`].full ||
                                      selectedCells[`${role.name}-admin`].read)
                                  }
                                />
                              }
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      {role.submenus &&
                        role.submenus.length > 0 &&
                        role.submenus.map((sub) => {
                          const isParentMenuNoAccess =
                            selectedCells[`${role.name}-admin`] &&
                            selectedCells[`${role.name}-admin`].none;

                          return (
                            <TableRow key={`${role.name}-${sub.id}`}>
                              <TableCell className={classes.tableCell}>
                                {sub.name !== "" ? (
                                  <Typography className={classes.subMenu}>
                                    {sub.name}
                                  </Typography>
                                ) : null}
                              </TableCell>

                              <TableCell className={classes.tableCell}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          selectedCells[
                                            `${role.name}-${sub.name}-admin`
                                          ]
                                            ? selectedCells[
                                                `${role.name}-${sub.name}-admin`
                                              ].full
                                            : false
                                        }
                                        onChange={(e) =>
                                          handleCellChange(
                                            `${role.name}-${sub.name}`,
                                            {
                                              full: e.target.checked,
                                            }
                                          )
                                        }
                                        sx={{ margin: 0 }}
                                        disabled={isParentMenuNoAccess}
                                      />
                                    }
                                  />
                                </Box>
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          selectedCells[
                                            `${role.name}-${sub.name}-admin`
                                          ]
                                            ? selectedCells[
                                                `${role.name}-${sub.name}-admin`
                                              ].read
                                            : false
                                        }
                                        onChange={(e) =>
                                          handleCellChange(
                                            `${role.name}-${sub.name}`,
                                            {
                                              read: e.target.checked,
                                            }
                                          )
                                        }
                                        sx={{ margin: 0 }}
                                        disabled={isParentMenuNoAccess}
                                      />
                                    }
                                  />
                                </Box>
                              </TableCell>

                              <TableCell className={classes.tableCell}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          selectedCells[
                                            `${role.name}-${sub.name}-admin`
                                          ]
                                            ? selectedCells[
                                                `${role.name}-${sub.name}-admin`
                                              ].none
                                            : false
                                        }
                                        onChange={(e) =>
                                          handleCellChange(
                                            `${role.name}-${sub.name}`,
                                            {
                                              none: e.target.checked,
                                            }
                                          )
                                        }
                                        sx={{ margin: 0 }}
                                        disabled={isParentMenuNoAccess}
                                      />
                                    }
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "8px",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={logSelectedMenuUUIDs}
                >
                  Submit
                </Button>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default RoleChartModal;
